import React, { useState, useEffect } from 'react';
import { Table, Select, Spin, message, Button, Tag } from 'antd';
import axios from 'axios';
import { getEndpoint } from '../utils/endpoints';

const { Option } = Select;

interface Client {
  client_name: string;
  description: string;
  settings: {
    managed_domains: string[];
    translation_directions: string[];
  };
  preferences?: Record<string, Record<string, { guidelines: string[]; reviewer_guidelines?: string[]; glossaries: string[]; memories: string[] }>>;
}

interface Guideline {
  guideline_name: string;
}

interface Glossary {
  glossary_name: string;
}

interface Memory {
  memory_name: string;
}

const ClientGuidelineGlossaryMapping: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [guidelines, setGuidelines] = useState<Guideline[]>([]);
  const [glossaries, setGlossaries] = useState<Glossary[]>([]);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(false);
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  const [dataSource, setDataSource] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clientsResponse, guidelinesResponse, glossariesResponse, memoriesResponse] = await Promise.all([
        axios.get(`${getEndpoint('GLOSS')}/getClients/`),
        axios.get(`${getEndpoint('GLOSS')}/getGuidelines/`),
        axios.get(`${getEndpoint('GLOSS')}/getGlossaries/`),
        axios.get(`${getEndpoint('BACKEND')}/getMemories/`),
      ]);
      setClients(clientsResponse.data);
      setGuidelines(guidelinesResponse.data);
      setGlossaries(glossariesResponse.data);
      setMemories(memoriesResponse.data);

      const rows = clientsResponse.data.flatMap((client: Client) => {
        const rows: any[] = [];
        const preferences = client.preferences || {}; // Fallback if preferences is undefined
        client.settings.managed_domains.forEach((domain) => {
          client.settings.translation_directions.forEach((direction) => {
            const key = `${client.client_name}-${domain}-${direction}`;
            const guidelines = preferences[domain]?.[direction]?.guidelines || [];
            const reviewer_guidelines = preferences[domain]?.[direction]?.reviewer_guidelines || [];
            const glossaries = preferences[domain]?.[direction]?.glossaries || [];
            const memories = preferences[domain]?.[direction]?.memories || [];
            rows.push({
              key,
              client_name: client.client_name,
              description: client.description,
              domain,
              direction,
              guidelines,
              reviewer_guidelines,
              glossaries,
              memories,
            });
          });
        });
        return rows;
      });

      setDataSource(rows);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to fetch data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key: string) => {
    const row = dataSource.find((row) => row.key === key);
    if (!row) return;

    setLoading(true);
    try {
    await axios.post(`${getEndpoint('GLOSS')}/saveClientSettings/`, {
      client_name: row.client_name,
      domain: row.domain,
      direction: row.direction,
      guidelines: row.guidelines,
      reviewer_guidelines: row.reviewer_guidelines,
      glossaries: row.glossaries,
      memories: row.memories,
    });
      message.success(`Changes for row ${key} saved successfully!`);
      setChanges((prev) => ({ ...prev, [key]: false }));
    } catch (error) {
      console.error('Error saving data:', error);
      message.error('Failed to save changes.');
    } finally {
      setLoading(false);
    }
  };

  const uniqueClientNames = Array.from(new Set(dataSource.map(row => row.client_name)));
  const uniqueDomains = Array.from(new Set(dataSource.map(row => row.domain)));
  const uniqueDirections = Array.from(new Set(dataSource.map(row => row.direction)));

  const columns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      filters: uniqueClientNames.map(name => ({ text: name, value: name })),
      onFilter: (value, record) => record.client_name === value,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      filters: uniqueDomains.map(domain => ({ text: domain, value: domain })),
      onFilter: (value, record) => record.domain === value,
      render: (domain: string) => <Tag>{domain}</Tag>,
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      filters: uniqueDirections.map(direction => ({ text: direction, value: direction })),
      onFilter: (value, record) => record.direction === value,
      render: (direction: string) => <Tag>{direction}</Tag>,
    },
    {
      title: 'Guidelines',
      dataIndex: 'guidelines',
      key: 'guidelines',
      render: (_, record: any) => (
        <Select
          mode="tags"
          placeholder="Select Guidelines"
          value={record.guidelines}
          onChange={(value) => {
            setDataSource((prev) =>
              prev.map((row) => (row.key === record.key ? { ...row, guidelines: value } : row))
            );
            setChanges((prev) => ({ ...prev, [record.key]: true }));
          }}
          style={{ width: '100%' }}
        >
          {guidelines.map((guideline) => (
            <Option key={guideline.guideline_name} value={guideline.guideline_name}>
              {guideline.guideline_name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Reviewer Guidelines',
      dataIndex: 'reviewer_guidelines',
      key: 'reviewer_guidelines',
      render: (_, record: any) => (
        <Select
          mode="tags"
          placeholder="Select Reviewer Guidelines"
          value={record.reviewer_guidelines}
          onChange={(value) => {
            setDataSource((prev) =>
              prev.map((row) => (row.key === record.key ? { ...row, reviewer_guidelines: value } : row))
            );
            setChanges((prev) => ({ ...prev, [record.key]: true }));
          }}
          style={{ width: '100%' }}
        >
          {guidelines.map((guideline) => (
            <Option key={guideline.guideline_name} value={guideline.guideline_name}>
              {guideline.guideline_name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Glossaries',
      dataIndex: 'glossaries',
      key: 'glossaries',
      render: (_, record: any) => (
        <Select
          mode="tags"
          placeholder="Select Glossaries"
          value={record.glossaries}
          onChange={(value) => {
            setDataSource((prev) =>
              prev.map((row) => (row.key === record.key ? { ...row, glossaries: value } : row))
            );
            setChanges((prev) => ({ ...prev, [record.key]: true }));
          }}
          style={{ width: '100%' }}
        >
          {glossaries.map((glossary) => (
            <Option key={glossary.glossary_name} value={glossary.glossary_name}>
              {glossary.glossary_name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Memories',
      dataIndex: 'memories',
      key: 'memories',
      render: (_, record: any) => (
        <Select
          mode="tags"
          placeholder="Select Memories"
          value={record.memories}
          onChange={(value) => {
            setDataSource((prev) =>
              prev.map((row) => (row.key === record.key ? { ...row, memories: value } : row))
            );
            setChanges((prev) => ({ ...prev, [record.key]: true }));
          }}
          style={{ width: '100%' }}
        >
          {memories.map((memory) => (
            <Option key={memory.memory_name} value={memory.memory_name}>
              {memory.memory_name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: any) => (
        <Button
          type="primary"
          onClick={() => handleSave(record.key)}
          disabled={!changes[record.key]}
        >
          Save
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Table columns={columns} dataSource={dataSource} pagination={false} />
    </Spin>
  );
};

export default ClientGuidelineGlossaryMapping;
